import "./DatasetPreview.css";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiRequest, logout } from "../utils/api";
import Plot from "react-plotly.js";

function DatasetPreview() {
  const [edaData, setEdaData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [time, setTime] = useState("");
  const [activeTab, setActiveTab] = useState("overview");
  const [showCleaningModal, setShowCleaningModal] = useState(false);
  const [columnMethods, setColumnMethods] = useState({}); // Column name -> cleaning method
  const [cleanedDatasetName, setCleanedDatasetName] = useState("");
  const [cleaningInProgress, setCleaningInProgress] = useState(false);
  const [cleaningSuccess, setCleaningSuccess] = useState(false);
  const [showHeatmap, setShowHeatmap] = useState(false);
  const [showSplitModal, setShowSplitModal] = useState(false);
  const [use100Percent, setUse100Percent] = useState(true); // true = use 100%, false = use split
  const [trainRatio, setTrainRatio] = useState(70);
  const [testRatio, setTestRatio] = useState(30);
  const [splitConfigured, setSplitConfigured] = useState(false); // Track if split has been chosen
  const [allColumns, setAllColumns] = useState([]); // Store all column names for cleaning
  const [heatmapData, setHeatmapData] = useState(null); // Store heatmap from backend
  const navigate = useNavigate();

  useEffect(() => {
    // Always fetch original dataset on initial load (not cleaned)
    // Reset any previous state
    setEdaData(null);
    setShowCleaningModal(false);
    setCleaningSuccess(false);
    fetchEDA(false); // Explicitly pass false to ensure we get original dataset
    const interval = setInterval(() => {
      const currentTime = new Date();
      const hours = currentTime.getHours() % 12 || 12;
      const minutes = currentTime.getMinutes().toString().padStart(2, "0");
      const seconds = currentTime.getSeconds().toString().padStart(2, "0");
      const ampm = currentTime.getHours() >= 12 ? "PM" : "AM";
      setTime(`${hours}:${minutes}:${seconds} ${ampm}`);
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
      navigate('/login');
    }
  };

  const handleCleanData = async () => {
    setCleaningInProgress(true);
    try {
      const response = await apiRequest('/api/dataset/clean', {
        method: 'POST',
        body: {
          column_methods: columnMethods,
          dataset_name: cleanedDatasetName || undefined
        }
      });

      if (response.status === 'success') {
        setCleaningSuccess(true);
        // Wait a moment to show success message, then show split selection modal
        setTimeout(async () => {
          setShowCleaningModal(false);
          // Refresh the EDA data to show the cleaned dataset
          await fetchEDA(true); // Pass true to show cleaned dataset
          // Show split selection modal
          setShowSplitModal(true);
        }, 1500);
      } else {
        setError('Failed to clean dataset: ' + (response.message || 'Unknown error'));
        setCleaningInProgress(false);
      }
    } catch (err) {
      console.error('Error cleaning data:', err);
      setError('Failed to clean dataset: ' + (err.message || 'Unknown error'));
      setCleaningInProgress(false);
    }
  };

  const handleColumnMethodChange = (columnName, method) => {
    setColumnMethods(prev => ({
      ...prev,
      [columnName]: method
    }));
  };

  // Fetch heatmap data from backend
  const fetchHeatmap = async () => {
    try {
      const data = await apiRequest('/api/dataset/missing-values');
      if (data.status === 'success' && data.heatmap_plot) {
        setHeatmapData(data.heatmap_plot);
      } else {
        setHeatmapData(null);
      }
    } catch (error) {
      console.error('Error fetching heatmap:', error);
      setHeatmapData(null);
    }
  };

  // Fetch heatmap when user clicks "View Heatmap"
  const handleViewHeatmap = () => {
    if (!showHeatmap && !heatmapData) {
      fetchHeatmap();
    }
    setShowHeatmap(!showHeatmap);
  };

  const fetchEDA = async (showCleaned = false) => {
    setLoading(true);
    setError(null);
    try {
      // Always explicitly set the parameter to ensure we get the right dataset
      const url = showCleaned ? '/api/dataset/eda?show_cleaned=true' : '/api/dataset/eda?show_cleaned=false';
      console.log('DEBUG: Fetching EDA with showCleaned=', showCleaned, 'URL=', url);
      const data = await apiRequest(url);
      if (data.status === 'success') {
        console.log('DEBUG: EDA data received - file_name:', data.dataset_info?.file_name, 'has_missing_values:', data.has_missing_values);
        setEdaData(data);
        setAllColumns(data.dataset_info?.column_names || []); // Store all column names
        // Show cleaning modal if missing values are found (mandatory) - only for original datasets
        if (!showCleaned && data.has_missing_values) {
          setShowCleaningModal(true);
          // Initialize column methods with default values for ALL columns
          const initialMethods = {};
          const allColumns = data.dataset_info?.all_columns || Object.keys(data.missing_values_summary || {});
          
          // For columns with missing values, set default method
          Object.keys(data.missing_values_summary || {}).forEach(col => {
            const isNumeric = data.dataset_info?.numeric_columns?.includes(col);
            initialMethods[col] = isNumeric ? 'mean' : 'mode';
          });
          
          // For columns without missing values, default to 'skip'
          allColumns.forEach(col => {
            if (!(col in initialMethods)) {
              initialMethods[col] = 'skip';
            }
          });
          
          setColumnMethods(initialMethods);
        } else if (!showCleaned && !data.has_missing_values) {
          // No missing values - show split selection modal directly
          setShowSplitModal(true);
        }
      } else {
        setError('Failed to load dataset preview');
      }
    } catch (err) {
      console.error('Error fetching EDA:', err);
      // Provide more helpful error messages
      if (err.message.includes('404') || err.message.includes('Not Found')) {
        setError('Endpoint not found. Please ensure the backend server has been restarted to load the new EDA endpoint.');
      } else if (err.message.includes('No dataset found')) {
        setError('No dataset found. Please upload a dataset first.');
      } else {
        setError(err.message || 'Failed to load dataset preview');
      }
    } finally {
      setLoading(false);
    }
  };

  const renderPreviewTable = () => {
    if (!edaData || !edaData.preview || edaData.preview.length === 0) {
      return <p>No preview data available</p>;
    }

    const columns = Object.keys(edaData.preview[0]);
    
    return (
      <div className="table-container">
        <table className="preview-table">
          <thead>
            <tr>
              {columns.map((col, idx) => (
                <th key={idx}>{col}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {edaData.preview.map((row, rowIdx) => (
              <tr key={rowIdx}>
                {columns.map((col, colIdx) => (
                  <td key={colIdx}>
                    {row[col] === null || row[col] === undefined 
                      ? <span className="null-value">N/A</span>
                      : typeof row[col] === 'number' 
                        ? row[col].toLocaleString()
                        : String(row[col])}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderDescriptiveStats = () => {
    if (!edaData || !edaData.descriptive_stats || Object.keys(edaData.descriptive_stats).length === 0) {
      return <p>No numeric columns found for descriptive statistics</p>;
    }

    const stats = edaData.descriptive_stats;
    const columns = Object.keys(stats);
    const metrics = ['count', 'mean', 'std', 'min', '25%', '50%', '75%', 'max'];

    return (
      <div className="stats-container">
        <table className="stats-table">
          <thead>
            <tr>
              <th>Metric</th>
              {columns.map((col, idx) => (
                <th key={idx}>{col}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {metrics.map((metric) => (
              <tr key={metric}>
                <td className="metric-label">{metric}</td>
                {columns.map((col, idx) => (
                  <td key={idx}>
                    {typeof stats[col][metric] === 'number' 
                      ? stats[col][metric].toLocaleString(undefined, { maximumFractionDigits: 4 })
                      : stats[col][metric]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderCorrelationMatrix = () => {
    if (!edaData || !edaData.plots || !edaData.plots.correlation_matrix) {
      return <p>Correlation matrix not available (requires at least 2 numeric columns)</p>;
    }

    return (
      <div className="plot-container correlation-matrix-container">
        <div className="plot-wrapper">
          <Plot
            data={edaData.plots.correlation_matrix.data}
            layout={{
              ...edaData.plots.correlation_matrix.layout,
              width: 800,
              height: 600,
              margin: { l: 100, r: 100, t: 50, b: 100 }
            }}
            config={{ responsive: true, displayModeBar: true }}
            style={{ width: '100%', maxWidth: '800px', height: '600px', margin: '0 auto' }}
          />
        </div>
      </div>
    );
  };

  const renderScatterPlots = () => {
    if (!edaData || !edaData.plots || !edaData.plots.scatter_plots || Object.keys(edaData.plots.scatter_plots).length === 0) {
      return <p>Scatter plots not available (requires at least 2 numeric columns)</p>;
    }

    const scatterPlots = edaData.plots.scatter_plots;
    const plotKeys = Object.keys(scatterPlots);

    return (
      <div className="plots-grid">
        {plotKeys.map((key, idx) => (
          <div key={idx} className="plot-item">
            <Plot
              data={scatterPlots[key].data}
              layout={scatterPlots[key].layout}
              config={{ responsive: true, displayModeBar: true }}
              style={{ width: '100%', height: '400px' }}
            />
          </div>
        ))}
      </div>
    );
  };

  const renderDistributionPlots = () => {
    if (!edaData || !edaData.plots || !edaData.plots.distribution_plots || Object.keys(edaData.plots.distribution_plots).length === 0) {
      return <p>Distribution plots not available</p>;
    }

    const distPlots = edaData.plots.distribution_plots;
    const plotKeys = Object.keys(distPlots);

    return (
      <div className="plots-grid">
        {plotKeys.map((key, idx) => (
          <div key={idx} className="plot-item">
            <Plot
              data={distPlots[key].data}
              layout={distPlots[key].layout}
              config={{ responsive: true, displayModeBar: true }}
              style={{ width: '100%', height: '400px' }}
            />
          </div>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="dataset-preview">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading dataset preview...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dataset-preview">
        <div className="error-container">
          <h2>Error Loading Dataset</h2>
          <p>{error}</p>
          <button onClick={() => navigate('/auto-ml')} className="back-button">
            Upload New Dataset
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="dataset-preview">
      {/* Top Navigation Bar */}
      <div className="top-nav">
        <div className="nav-content">
          <div className="nav-left-group">
            <div className="nav-logo" onClick={() => navigate("/")} style={{ cursor: "pointer" }}>
              <img src="/HDASLogo.png" alt="HDAS" style={{ height: "80px", width: "auto" }} />
            </div>
            <button className="back-nav-button" onClick={() => navigate("/dashboard")}>
              Dashboard
            </button>
          </div>
          <div className="nav-right">
            <div className="digital-clock">{time}</div>
            <button onClick={handleLogout} className="logout-button">Logout</button>
          </div>
        </div>
      </div>

      {/* Header Section */}
      <div className="page-header">
        <div className="header-content">
          <h1>Dataset Preview & Analysis</h1>
          <p className="header-subtitle">
            {edaData?.dataset_info?.file_name || "Dataset"}
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="page-content">
        {/* Dataset Info Cards */}
        {edaData?.dataset_info && (
          <div className="info-cards">
            <div className="info-card">
              <div className="info-icon icon-rows"></div>
              <div className="info-content">
                <div className="info-value">{edaData.dataset_info.rows.toLocaleString()}</div>
                <div className="info-label">Total Rows</div>
              </div>
            </div>
            <div className="info-card">
              <div className="info-icon icon-columns"></div>
              <div className="info-content">
                <div className="info-value">{edaData.dataset_info.columns}</div>
                <div className="info-label">Total Columns</div>
              </div>
            </div>
            <div className="info-card">
              <div className="info-icon icon-numeric"></div>
              <div className="info-content">
                <div className="info-value">{edaData.dataset_info.numeric_columns?.length || 0}</div>
                <div className="info-label">Numeric Columns</div>
              </div>
            </div>
            <div className="info-card">
              <div className="info-icon icon-memory"></div>
              <div className="info-content">
                <div className="info-value">{edaData.dataset_info.memory_usage}</div>
                <div className="info-label">Memory Usage</div>
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="tabs-container">
          <button
            className={`tab ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            Data Preview
          </button>
          <button
            className={`tab ${activeTab === 'stats' ? 'active' : ''}`}
            onClick={() => setActiveTab('stats')}
          >
            Descriptive Statistics
          </button>
          <button
            className={`tab ${activeTab === 'correlation' ? 'active' : ''}`}
            onClick={() => setActiveTab('correlation')}
          >
            Correlation Matrix
          </button>
          <button
            className={`tab ${activeTab === 'scatter' ? 'active' : ''}`}
            onClick={() => setActiveTab('scatter')}
          >
            Scatter Plots
          </button>
          <button
            className={`tab ${activeTab === 'distribution' ? 'active' : ''}`}
            onClick={() => setActiveTab('distribution')}
          >
            Distributions
          </button>
        </div>

        {/* Tab Content */}
        <div className="tab-content">
          {activeTab === 'overview' && (
            <div className="tab-panel">
              <h2>Dataset Preview (First 10 Rows)</h2>
              {renderPreviewTable()}
            </div>
          )}

          {activeTab === 'stats' && (
            <div className="tab-panel">
              <h2>Descriptive Statistics</h2>
              <p className="section-description">
                Statistical summary for numeric columns including mean, standard deviation, quartiles, and min/max values.
              </p>
              {renderDescriptiveStats()}
            </div>
          )}

          {activeTab === 'correlation' && (
            <div className="tab-panel">
              <h2>Correlation Matrix</h2>
              <p className="section-description">
                Correlation coefficients between numeric features. Values range from -1 to 1, where 1 indicates perfect positive correlation and -1 indicates perfect negative correlation.
              </p>
              {renderCorrelationMatrix()}
            </div>
          )}

          {activeTab === 'scatter' && (
            <div className="tab-panel">
              <h2>Scatter Plots</h2>
              <p className="section-description">
                Scatter plots showing relationships between top correlated numeric feature pairs.
              </p>
              {renderScatterPlots()}
            </div>
          )}

          {activeTab === 'distribution' && (
            <div className="tab-panel">
              <h2>Distribution Plots</h2>
              <p className="section-description">
                Histograms showing the distribution of numeric features in your dataset.
              </p>
              {renderDistributionPlots()}
            </div>
          )}
        </div>

        {/* Action Buttons */}
        {!showSplitModal && (
          <div className="action-buttons">
            {splitConfigured ? (
              <>
                <div className="split-configured-info">
                  <div className="info-badge">
                    ✓ Split Configuration: {use100Percent ? '100% Training (Upload Test Later)' : `${trainRatio}% Train / ${testRatio}% Test`}
                  </div>
                  <button onClick={() => setSplitConfigured(false)} className="change-split-button">
                    Change Split
                  </button>
                </div>
                <button onClick={() => navigate('/analysis-selection')} className="primary-button">
                  Continue to Analysis Selection
                </button>
                <button onClick={() => navigate('/auto-ml')} className="secondary-button">
                  Upload Different Dataset
                </button>
              </>
            ) : (
              <>
                <button onClick={() => navigate('/analysis-selection')} className="primary-button">
                  Continue to Analysis Selection
                </button>
                <button onClick={() => navigate('/auto-ml')} className="secondary-button">
                  Upload Different Dataset
                </button>
              </>
            )}
          </div>
        )}
      </div>

      {/* Data Cleaning Modal - Mandatory, cannot be closed */}
      {showCleaningModal && edaData?.has_missing_values && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>Missing Values Detected - Cleaning Required</h2>
              <p className="mandatory-notice">You must clean the dataset before proceeding</p>
            </div>
            
            <div className="modal-body">
              {!cleaningSuccess ? (
                <>
                  <div className="warning-box">
                    <p><strong>Warning:</strong> Your dataset contains missing values that may affect analysis quality.</p>
                    <div className="missing-values-summary">
                      <p><strong>Missing Values Summary:</strong></p>
                      <ul>
                        {Object.entries(edaData.missing_values_summary || {}).map(([col, count]) => (
                          <li key={col}>
                            <strong>{col}:</strong> {count} missing value{count !== 1 ? 's' : ''}
                          </li>
                        ))}
                      </ul>
                      <p><strong>Total Missing Values:</strong> {Object.values(edaData.missing_values_summary || {}).reduce((a, b) => a + b, 0)}</p>
                      <button
                        className="view-heatmap-button"
                        onClick={handleViewHeatmap}
                      >
                        {showHeatmap ? 'Hide Heatmap' : 'View Heatmap'}
                      </button>
                    </div>
                    
                    {/* Missing Values Heatmap - Full Screen Modal */}
                    {showHeatmap && edaData && (
                      <div className="heatmap-fullscreen-overlay" onClick={() => setShowHeatmap(false)}>
                        <div className="heatmap-fullscreen-content" onClick={(e) => e.stopPropagation()}>
                          <div className="heatmap-fullscreen-header">
                            <h2>Missing Values Heatmap</h2>
                            <button 
                              className="heatmap-close-button"
                              onClick={() => setShowHeatmap(false)}
                              title="Close"
                            >
                              ×
                            </button>
                          </div>
                          <div className="heatmap-fullscreen-body">
                            <h3 className="heatmap-title">Missing Values Percentage by Column</h3>
                            <p className="heatmap-description">
                              Visual heatmap showing the percentage of missing values for each column. <strong style={{color: '#ffeb3b'}}>Yellow</strong> indicates low missing percentage, <strong style={{color: '#ff6b00'}}>orange</strong> indicates moderate, and <strong style={{color: '#c62828'}}>red</strong> indicates high missing percentage. The percentage value is displayed in each cell.
                            </p>
                            <div className="heatmap-fullscreen-plot">
                              {heatmapData ? (
                                <Plot
                                  data={heatmapData.data}
                                  layout={heatmapData.layout}
                                  config={{
                                    displayModeBar: true,
                                    responsive: true,
                                    modeBarButtonsToRemove: ['pan2d', 'lasso2d', 'select2d'],
                                    displaylogo: false
                                  }}
                                />
                              ) : (
                                <p>Loading heatmap...</p>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="cleaning-options">
                    <h3>Choose Cleaning Method for Each Column:</h3>
                    <p className="cleaning-instructions">
                      Select how to handle missing values for each column, or remove columns entirely. You can use different methods for different columns.
                    </p>
                    <div className="columns-cleaning-list">
                      {/* Show all columns, not just those with missing values */}
                      {allColumns.map((colName) => {
                        const isNumeric = edaData.dataset_info?.numeric_columns?.includes(colName);
                        const missingCount = edaData.missing_values_summary?.[colName] || 0;
                        const currentMethod = columnMethods[colName] || (missingCount > 0 ? (isNumeric ? 'mean' : 'mode') : 'skip');
                        
                        return (
                          <div key={colName} className={`column-cleaning-item ${currentMethod === 'remove_column' ? 'column-to-remove' : ''}`}>
                            <div className="column-header">
                              <strong>{colName}</strong>
                              {missingCount > 0 && (
                                <span className="missing-count">{missingCount} missing value{missingCount !== 1 ? 's' : ''}</span>
                              )}
                              {missingCount === 0 && (
                                <span className="no-missing">No missing values</span>
                              )}
                              {isNumeric && <span className="column-type-badge numeric">Numeric</span>}
                              {!isNumeric && <span className="column-type-badge categorical">Categorical</span>}
                            </div>
                            <div className="column-methods">
                              {missingCount > 0 && (
                                <>
                                  <label className="method-option-small">
                                    <input
                                      type="radio"
                                      name={`method-${colName}`}
                                      value="mean"
                                      checked={currentMethod === "mean"}
                                      onChange={(e) => handleColumnMethodChange(colName, e.target.value)}
                                      disabled={cleaningInProgress || !isNumeric}
                                    />
                                    <span>Mean {!isNumeric && '(N/A)'}</span>
                                  </label>
                                  <label className="method-option-small">
                                    <input
                                      type="radio"
                                      name={`method-${colName}`}
                                      value="median"
                                      checked={currentMethod === "median"}
                                      onChange={(e) => handleColumnMethodChange(colName, e.target.value)}
                                      disabled={cleaningInProgress || !isNumeric}
                                    />
                                    <span>Median {!isNumeric && '(N/A)'}</span>
                                  </label>
                                  <label className="method-option-small">
                                    <input
                                      type="radio"
                                      name={`method-${colName}`}
                                      value="mode"
                                      checked={currentMethod === "mode"}
                                      onChange={(e) => handleColumnMethodChange(colName, e.target.value)}
                                      disabled={cleaningInProgress}
                                    />
                                    <span>Mode</span>
                                  </label>
                                  <label className="method-option-small">
                                    <input
                                      type="radio"
                                      name={`method-${colName}`}
                                      value="remove"
                                      checked={currentMethod === "remove"}
                                      onChange={(e) => handleColumnMethodChange(colName, e.target.value)}
                                      disabled={cleaningInProgress}
                                    />
                                    <span>Remove Rows</span>
                                  </label>
                                  <label className="method-option-small">
                                    <input
                                      type="radio"
                                      name={`method-${colName}`}
                                      value="skip"
                                      checked={currentMethod === "skip"}
                                      onChange={(e) => handleColumnMethodChange(colName, e.target.value)}
                                      disabled={cleaningInProgress}
                                    />
                                    <span>Skip</span>
                                  </label>
                                </>
                              )}
                              {/* Remove Column option - available for all columns */}
                              <label className="method-option-small remove-column-option">
                                <input
                                  type="radio"
                                  name={`method-${colName}`}
                                  value="remove_column"
                                  checked={currentMethod === "remove_column"}
                                  onChange={(e) => handleColumnMethodChange(colName, e.target.value)}
                                  disabled={cleaningInProgress}
                                />
                                <span>Remove Column</span>
                              </label>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <div className="dataset-name-input">
                    <label htmlFor="cleanedName">
                      <strong>Cleaned Dataset Name (Optional):</strong>
                    </label>
                    <input
                      id="cleanedName"
                      type="text"
                      placeholder="Leave empty for auto-generated name"
                      value={cleanedDatasetName}
                      onChange={(e) => setCleanedDatasetName(e.target.value)}
                      disabled={cleaningInProgress}
                    />
                  </div>
                </>
              ) : (
                <div className="success-box">
                  <p>Dataset cleaned and saved successfully!</p>
                  <p>The cleaned dataset has been saved to your dashboard.</p>
                </div>
              )}
            </div>

            <div className="modal-footer">
              {!cleaningSuccess ? (
                <button
                  className="primary-button"
                  onClick={handleCleanData}
                  disabled={cleaningInProgress}
                  style={{ width: '100%' }}
                >
                  {cleaningInProgress ? "Cleaning..." : "Clean & Save Dataset"}
                </button>
              ) : (
                <div className="success-navigation-info">
                  <p>Dataset cleaned successfully! Refreshing preview...</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Train-Test Split Selection Modal - Shown after cleaning */}
      {showSplitModal && (
        <div className="modal-overlay">
          <div className="modal-content split-selection-modal">
            <div className="modal-header">
              <h2>Choose Data Split Strategy</h2>
              <p className="mandatory-notice">Select how you want to use your data for training and testing</p>
            </div>
            
            <div className="modal-body">
              <div className="split-options">
                {/* Option A: Use 100% of data */}
                <div 
                  className={`split-option ${use100Percent ? 'selected' : ''}`}
                  onClick={() => setUse100Percent(true)}
                >
                  <div className="split-option-header">
                    <input
                      type="radio"
                      name="split-option"
                      checked={use100Percent}
                      onChange={() => setUse100Percent(true)}
                    />
                    <h3>Use 100% of Data for Training</h3>
                  </div>
                  <p className="split-option-description">
                    Use all your cleaned data for training. You will upload a separate test CSV file during the prediction phase.
                  </p>
                </div>

                {/* Option B: Choose split ratio */}
                <div 
                  className={`split-option ${!use100Percent ? 'selected' : ''}`}
                  onClick={() => setUse100Percent(false)}
                >
                  <div className="split-option-header">
                    <input
                      type="radio"
                      name="split-option"
                      checked={!use100Percent}
                      onChange={() => setUse100Percent(false)}
                    />
                    <h3>Split Data into Train/Test Sets</h3>
                  </div>
                  <p className="split-option-description">
                    Automatically split your data. No test CSV upload needed during prediction.
                  </p>
                  
                  {!use100Percent && (
                    <div className="split-ratio-inputs">
                      <div className="ratio-input-group">
                        <label>Training Set:</label>
                        <input
                          type="number"
                          min="10"
                          max="90"
                          value={trainRatio}
                          onChange={(e) => {
                            const train = parseInt(e.target.value) || 70;
                            const test = 100 - train;
                            setTrainRatio(train);
                            setTestRatio(test);
                          }}
                        />
                        <span>%</span>
                      </div>
                      <div className="ratio-separator">/</div>
                      <div className="ratio-input-group">
                        <label>Test Set:</label>
                        <input
                          type="number"
                          min="10"
                          max="90"
                          value={testRatio}
                          onChange={(e) => {
                            const test = parseInt(e.target.value) || 30;
                            const train = 100 - test;
                            setTestRatio(test);
                            setTrainRatio(train);
                          }}
                        />
                        <span>%</span>
                      </div>
                    </div>
                  )}

                  {!use100Percent && (
                    <div className="common-split-buttons">
                      <button 
                        className="quick-split-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          setTrainRatio(70);
                          setTestRatio(30);
                        }}
                      >
                        70/30
                      </button>
                      <button 
                        className="quick-split-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          setTrainRatio(80);
                          setTestRatio(20);
                        }}
                      >
                        80/20
                      </button>
                      <button 
                        className="quick-split-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          setTrainRatio(90);
                          setTestRatio(10);
                        }}
                      >
                        90/10
                      </button>
                      <button 
                        className="quick-split-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          setTrainRatio(50);
                          setTestRatio(50);
                        }}
                      >
                        50/50
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button
                className="primary-button"
                onClick={() => {
                  // Store split choice in localStorage
                  const splitConfig = {
                    use_100_percent: use100Percent,
                    train_ratio: trainRatio,
                    test_ratio: testRatio
                  };
                  localStorage.setItem('dataSplitConfig', JSON.stringify(splitConfig));
                  setShowSplitModal(false);
                  setSplitConfigured(true);
                }}
                style={{ width: '100%' }}
              >
                Confirm Split Configuration
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DatasetPreview;

